import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { excluirFaturamento } from '../../services/prontuario';
import { toggleItem } from './helpers';
import { abrirPdf, documentoListagem } from './pdf';
import useListagemTab from './useListagemTab';

function codigoEmissor(medico, funcionario) {
  return (
    (medico && medico.idFunc) || (funcionario && funcionario.codFunc) || 'ICM'
  );
}

export default function AbaListagem({
  ativo,
  tituloPdf,
  convenios,
  procedimentos,
}) {
  const medico = useSelector(state => state.doctor.profile);
  const { funcionario } = useSelector(state => state.funcionario);
  const hoje = new Date().toISOString().slice(0, 10);
  const [dataInicial, setDataInicial] = useState(hoje);
  const [dataFinal, setDataFinal] = useState(hoje);
  const [conveniosSel, setConveniosSel] = useState(convenios);
  const [procedimentosSel, setProcedimentosSel] = useState(procedimentos);
  const [itemParaExcluir, setItemParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  const { dados, error, isLoading, mutate } = useListagemTab({
    ativo,
    dataInicial,
    dataFinal,
    convenios: conveniosSel,
    procedimentos: procedimentosSel,
  });

  const [desmarcados, setDesmarcados] = useState(new Set());

  // Limpa a seleção quando os dados mudam (nova pesquisa)
  useEffect(() => {
    setDesmarcados(new Set());
  }, [dados]);

  const handleToggleLinha = (codFatura) => {
    setDesmarcados(prev => {
      const next = new Set(prev);
      if (next.has(codFatura)) {
        next.delete(codFatura);
      } else {
        next.add(codFatura);
      }
      return next;
    });
  };

  const todosSelecionados = dados && dados.length > 0 && desmarcados.size === 0;

  const handleToggleTodos = () => {
    if (todosSelecionados) {
      setDesmarcados(new Set(dados.map(d => d.CODFATURA)));
    } else {
      setDesmarcados(new Set());
    }
  };

  const handleImprimir = () => {
    const dadosImpressao = dados ? dados.filter(d => !desmarcados.has(d.CODFATURA)) : [];

    if (!dadosImpressao || dadosImpressao.length === 0) {
      toast.warn('Nenhum dado selecionado para gerar listagem!');
      return;
    }

    abrirPdf(
      documentoListagem({
        dados: dadosImpressao,
        titulo: tituloPdf,
        dataIni: dataInicial,
        dataFim: dataFinal,
        codFuncEmissor: codigoEmissor(medico, funcionario),
        now: new Date().toLocaleString('pt-BR', { hour12: false }),
      })
    );
  };

  const handleConfirmarExclusao = async () => {
    if (!itemParaExcluir || excluindo) return;

    setExcluindo(true);
    try {
      const resultado = await excluirFaturamento({
        codFatura: itemParaExcluir.CODFATURA,
        codLaudo: itemParaExcluir.CODLAUDO,
        codProcedi: itemParaExcluir.CODPROCEDI,
      });
      toast.success(resultado.message || 'Faturamento excluído com sucesso!');
      setItemParaExcluir(null);
      await mutate();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir faturamento!');
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <>
      <Form className="mb-3 pb-3 border-bottom">
        <div className="row g-2 align-items-end">
          <div className="col-md-2 col-6">
            <Form.Label className="fw-semibold mb-1">Data Inicial</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={dataInicial}
              onChange={e => setDataInicial(e.target.value)}
            />
          </div>
          <div className="col-md-2 col-6">
            <Form.Label className="fw-semibold mb-1">Data Final</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={dataFinal}
              onChange={e => setDataFinal(e.target.value)}
            />
          </div>
          <div className="col-md-4 col-12">
            <Form.Label className="fw-semibold mb-1">Procedimentos</Form.Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {procedimentos.map(p => (
                <Form.Check
                  key={p}
                  inline
                  label={p}
                  type="checkbox"
                  checked={procedimentosSel.includes(p)}
                  onChange={e =>
                    toggleItem(setProcedimentosSel, p, e.target.checked)
                  }
                />
              ))}
            </div>
          </div>
          <div className="col-md-4 col-12">
            <Form.Label className="fw-semibold mb-1">Convênios</Form.Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {convenios.map(c => (
                <Form.Check
                  key={c}
                  inline
                  label={c}
                  type="checkbox"
                  checked={conveniosSel.includes(c)}
                  onChange={e =>
                    toggleItem(setConveniosSel, c, e.target.checked)
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </Form>
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="dark"
          size="sm"
          disabled={isLoading || (dados && dados.length === 0)}
          onClick={handleImprimir}
        >
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            `Gerar Listagem (${dados ? dados.length - desmarcados.size : 0}/${dados ? dados.length : 0})`
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="danger">
          {error?.response?.data?.error || 'Erro ao carregar dados.'}
        </Alert>
      )}

      {isLoading ? (
        <div
          className="d-flex justify-content-center"
          style={{ minHeight: 120 }}
        >
          <Spinner animation="border" />
        </div>
      ) : dados.length > 0 ? (
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th style={{ width: '40px', textAlign: 'center' }}>
                <Form.Check
                  type="checkbox"
                  checked={todosSelecionados}
                  onChange={handleToggleTodos}
                />
              </th>
              <th>Data</th>
              <th>Paciente</th>
              <th>Realizou</th>
              <th>Opções</th>
            </tr>
          </thead>
          <tbody>
            {dados.map(item => (
              <tr
                key={item.CODFATURA}
                style={{ opacity: desmarcados.has(item.CODFATURA) ? 0.5 : 1 }}
              >
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={!desmarcados.has(item.CODFATURA)}
                    onChange={() => handleToggleLinha(item.CODFATURA)}
                  />
                </td>
                <td>{item.dataFormatada}</td>
                <td>{item.NOMEPAC}</td>
                <td>{item.REALIZOU}</td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    aria-label={`Excluir ${item.NOMEPAC}`}
                    disabled={excluindo}
                    onClick={() => setItemParaExcluir(item)}
                  >
                    Excluir
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Alert variant="info">Nenhum registro encontrado para o período.</Alert>
      )}

      <Modal
        show={Boolean(itemParaExcluir)}
        onHide={() => {
          if (!excluindo) setItemParaExcluir(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Excluir item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deseja realmente remover este item do relatório de faturamento?
          {itemParaExcluir && (
            <div className="mt-2">
              <strong>{itemParaExcluir.NOMEPAC}</strong>
              {itemParaExcluir.dataFormatada
                ? ` — ${itemParaExcluir.dataFormatada}`
                : ''}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setItemParaExcluir(null)}
            disabled={excluindo}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmarExclusao}
            disabled={excluindo}
          >
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
